import { KINDS, type NostrEvent } from "../types"
import { replyTargets } from "./feed"

/**
 * Parent of a note inside a thread (NIP-10):
 * `reply` marker when present, otherwise the root marker, otherwise the last
 * `e` tag (deprecated positional form).
 */
export function threadParentId(event: NostrEvent): string | undefined {
  if (event.kind !== KINDS.NOTE) return undefined
  const { replyId, rootId } = replyTargets(event)
  const parent = replyId ?? rootId
  if (!parent || parent === event.id) return undefined
  return parent
}

function byCreatedAt(a: NostrEvent, b: NostrEvent): number {
  if (a.created_at !== b.created_at) return a.created_at - b.created_at
  return a.id.localeCompare(b.id)
}

export interface ReplyTree {
  /** parent id -> direct replies (oldest first). */
  children: Map<string, NostrEvent[]>
  /** Replies whose parent could not be fetched (never silently re-parented). */
  orphans: NostrEvent[]
}

/**
 * Groups replies into a tree: parent event id -> direct children (oldest first).
 *
 * A reply whose `e` tags reference a parent that is not in `events` is returned
 * in `orphans` instead of being attached to the focused note, so the UI can say
 * "replying to an unavailable note" rather than showing a wrong hierarchy.
 */
export function buildReplyTree(
  events: NostrEvent[],
  fallbackParents: string[] = [],
): ReplyTree {
  const seen = new Set<string>()
  const children = new Map<string, NostrEvent[]>()
  const orphans: NostrEvent[] = []
  const fallback = fallbackParents[0]
  const known = new Set([...fallbackParents, ...events.map((event) => event.id)])

  for (const event of [...events].sort(byCreatedAt)) {
    if (seen.has(event.id)) continue
    seen.add(event.id)
    const parent = threadParentId(event) ?? fallback
    if (!parent) continue
    if (parent !== fallback && !known.has(parent)) {
      orphans.push(event)
      continue
    }
    const list = children.get(parent)
    if (list) list.push(event)
    else children.set(parent, [event])
  }

  for (const list of children.values()) list.sort(byCreatedAt)
  return { children, orphans }
}

/** Number of replies below a note (not counting itself). */
export function countDescendants(children: Map<string, NostrEvent[]>, id: string): number {
  const direct = children.get(id) ?? []
  return direct.reduce((sum, event) => sum + 1 + countDescendants(children, event.id), 0)
}

/**
 * Keeps only the events that belong to the focused note's subtree: their reply
 * chain passes through `noteId`, or they tag it directly. Replies that belong to
 * a sibling branch of the root thread are left out.
 */
export function descendantsOf(noteId: string, events: NostrEvent[]): NostrEvent[] {
  const byId = new Map(events.map((event) => [event.id, event]))
  const keep = new Set<string>()

  for (const event of events) {
    let parent = threadParentId(event)
    const guard = new Set<string>()
    while (parent && !guard.has(parent)) {
      if (parent === noteId) {
        keep.add(event.id)
        break
      }
      guard.add(parent)
      const parentEvent = byId.get(parent)
      if (!parentEvent) break
      parent = threadParentId(parentEvent)
    }
    if (!keep.has(event.id) && event.tags.some((tag) => tag[0] === "e" && tag[1] === noteId)) {
      keep.add(event.id)
    }
  }

  return events.filter((event) => keep.has(event.id))
}

export interface AncestorChain {
  /** Ancestors in display order (root first, direct parent last). */
  ancestors: NostrEvent[]
  /** Parent id that is referenced but not in the store yet, if any. */
  missingId?: string
}

/**
 * Walks up the reply chain from a note. Stops at the first parent that is not in
 * the store yet and reports it so the caller can fetch that id and re-run.
 */
export function collectAncestors(
  note: NostrEvent | undefined,
  getEvent: (id: string) => NostrEvent | undefined,
  limit = 20,
): AncestorChain {
  const ancestors: NostrEvent[] = []
  const seen = new Set<string>()
  let current = note

  while (current && ancestors.length < limit) {
    const parentId = threadParentId(current)
    if (!parentId || seen.has(parentId)) break
    seen.add(parentId)
    const parent = getEvent(parentId)
    if (!parent) return { ancestors, missingId: parentId }
    ancestors.unshift(parent)
    current = parent
  }

  return { ancestors }
}
